import { useEffect, useMemo, useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { ChevronDown, ChevronRight, Images, Plus, UploadCloud, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { fileToBase64, formatBytes } from '@/lib/fileToBase64';
import { useRoomMedia } from '@/hooks/useRoomMedia';
import { MediaThumb } from './MediaThumb';
import { MEDIA_KINDS, KIND_LABEL } from './mediaKinds';
import type {
  RenovationItemDto,
  RenovationMediaDto,
  RenovationMediaKind,
} from '@cc/types';

const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif,application/pdf';

interface Props {
  projectId: string;
  roomId: string | null;
  /** Items in the active room — for the per-upload item picker. */
  items: RenovationItemDto[];
  /** The room's pinned hero media id, if any. */
  heroMediaId?: string | null;
  /** Toggle pin/unpin for a given media id (pinning the same id unpins it). */
  onTogglePin?: (mediaId: string) => void;
}

/**
 * Collapsible Media card for the active room. Sits below ItemsTable in
 * Renovation.tsx. Drag-drop a file onto it (or use the upload button) to add;
 * filter the grid by kind via the chips row.
 */
export function RoomMediaPanel({ projectId, roomId, items, heroMediaId, onTogglePin }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [kindFilter, setKindFilter] = useState<RenovationMediaKind | null>(null);
  const [editing, setEditing] = useState<RenovationMediaDto | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { media, isLoading, isError, upload, update, remove } = useRoomMedia({
    projectId,
    roomId,
    kind: kindFilter,
  });

  const itemsById = useMemo(() => {
    const m = new Map<string, RenovationItemDto>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  // Per-kind count for the filter chip labels. Cheap O(n) walk over the
  // current room's media regardless of filter, derived from the data we
  // already have on screen.
  const counts = useMemo(() => {
    const out: Record<string, number> = { __all__: media.length };
    for (const m of media) out[m.kind] = (out[m.kind] ?? 0) + 1;
    return out;
  }, [media]);

  function handlePick(file: File | null) {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (max ${formatBytes(MAX_BYTES)}).`);
      return;
    }
    if (!ACCEPT.split(',').includes(file.type)) {
      toast.error('Unsupported file type. Use JPG, PNG, WebP, HEIC, GIF, or PDF.');
      return;
    }
    setPendingFile(file);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handlePick(file);
  }
  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    handlePick(file);
    e.target.value = '';
  }

  if (!roomId) return null;

  return (
    <Card>
      <CardContent className="p-3">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-between gap-2 text-left"
        >
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <Images className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Media</span>
            <span className="text-xs text-muted-foreground">({media.length})</span>
          </div>
          {expanded && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              <Plus className="h-3 w-3" /> Upload
            </Button>
          )}
        </button>

        {expanded && (
          <div className="mt-3 space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={onInputChange}
            />

            <div className="flex flex-wrap items-center gap-1.5">
              <FilterChip
                active={kindFilter === null}
                label={`All (${counts.__all__ ?? 0})`}
                onClick={() => setKindFilter(null)}
              />
              {MEDIA_KINDS.map((k) => (
                <FilterChip
                  key={k}
                  active={kindFilter === k}
                  label={`${KIND_LABEL[k]} (${counts[k] ?? 0})`}
                  onClick={() => setKindFilter(kindFilter === k ? null : k)}
                />
              ))}
            </div>

            {isLoading ? (
              <Skeleton className="h-32" />
            ) : isError ? (
              <p className="text-xs text-destructive">Could not load media.</p>
            ) : (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={cn(
                  'relative rounded-md border-2 border-dashed border-border p-2 transition-colors',
                  dragOver && 'border-primary bg-primary/5',
                )}
              >
                {media.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-6 text-center text-xs text-muted-foreground">
                    <UploadCloud className="h-5 w-5" />
                    <p>Drop a file here or click Upload.</p>
                    <p>JPG / PNG / WebP / HEIC / GIF / PDF, up to 20 MB.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                    {media.map((m) => (
                      <MediaThumb
                        key={m.id}
                        media={m}
                        itemsById={itemsById}
                        isHero={heroMediaId === m.id}
                        // Only images make sense as a hero. PDFs etc don't get
                        // the pin affordance.
                        onTogglePin={
                          onTogglePin && m.mimeType.startsWith('image/')
                            ? () => onTogglePin(m.id)
                            : undefined
                        }
                        onEdit={() => setEditing(m)}
                        onOpen={() => window.open(m.url, '_blank', 'noopener,noreferrer')}
                        onDelete={() => {
                          if (confirm(`Delete "${m.filename}"?`)) {
                            remove.mutate(m.id);
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
                {dragOver && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-primary/10">
                    <p className="text-sm font-medium text-primary">Drop to upload</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>

      <UploadDialog
        file={pendingFile}
        items={items}
        onCancel={() => setPendingFile(null)}
        onSubmit={async (form) => {
          if (!pendingFile) return;
          try {
            const contentBase64 = await fileToBase64(pendingFile);
            await upload.mutateAsync({
              filename: pendingFile.name,
              mimeType: pendingFile.type,
              contentBase64,
              kind: form.kind,
              itemId: form.itemId,
              caption: form.caption || null,
            });
            setPendingFile(null);
          } catch {
            // toast handled by mutation
          }
        }}
        pending={upload.isPending}
      />

      <EditDialog
        media={editing}
        items={items}
        onCancel={() => setEditing(null)}
        onSubmit={async (patch) => {
          if (!editing) return;
          await update.mutateAsync({ mediaId: editing.id, patch });
          setEditing(null);
        }}
        pending={update.isPending}
      />
    </Card>
  );
}

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] transition-colors',
        active
          ? 'border-primary bg-primary/15 text-foreground'
          : 'border-border bg-card text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

function UploadDialog({
  file,
  items,
  pending,
  onCancel,
  onSubmit,
}: {
  file: File | null;
  items: RenovationItemDto[];
  pending: boolean;
  onCancel: () => void;
  onSubmit: (form: { kind: RenovationMediaKind; itemId: string | null; caption: string }) => void;
}) {
  const [kind, setKind] = useState<RenovationMediaKind>('INSPIRATION');
  const [itemId, setItemId] = useState<string | 'none'>('none');
  const [caption, setCaption] = useState('');

  // Default kind based on mime: PDFs → FLOOR_PLAN (most common).
  useEffect(() => {
    if (!file) return;
    setKind(file.type === 'application/pdf' ? 'FLOOR_PLAN' : 'INSPIRATION');
    setItemId('none');
    setCaption('');
  }, [file]);

  return (
    <Dialog open={Boolean(file)} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload media</DialogTitle>
        </DialogHeader>
        {file && (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
              <p className="truncate font-medium">{file.name}</p>
              <p className="text-muted-foreground">
                {file.type || 'unknown'} · {formatBytes(file.size)}
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Kind</Label>
                <Select value={kind} onValueChange={(v) => setKind(v as RenovationMediaKind)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIA_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {KIND_LABEL[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Link to item (optional)</Label>
                <Select value={itemId} onValueChange={setItemId}>
                  <SelectTrigger>
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {items.map((it) => (
                      <SelectItem key={it.id} value={it.id}>
                        {it.activity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Caption (optional)</Label>
              <Textarea
                rows={2}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder='e.g. "showroom photo Sep 2024"'
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                kind,
                itemId: itemId === 'none' ? null : itemId,
                caption,
              })
            }
            disabled={pending}
          >
            <UploadCloud className="h-3.5 w-3.5" /> {pending ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  media,
  items,
  pending,
  onCancel,
  onSubmit,
}: {
  media: RenovationMediaDto | null;
  items: RenovationItemDto[];
  pending: boolean;
  onCancel: () => void;
  onSubmit: (patch: { kind: RenovationMediaKind; itemId: string | null; caption: string | null }) => void;
}) {
  const [kind, setKind] = useState<RenovationMediaKind>('INSPIRATION');
  const [itemId, setItemId] = useState<string | 'none'>('none');
  const [caption, setCaption] = useState('');

  useEffect(() => {
    if (!media) return;
    setKind(media.kind);
    setItemId(media.itemId ?? 'none');
    setCaption(media.caption ?? '');
  }, [media]);

  if (!media) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit media</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
            <p className="truncate font-medium">{media.filename}</p>
            <p className="text-muted-foreground">
              {media.mimeType} · {formatBytes(media.sizeBytes)}
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Kind</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as RenovationMediaKind)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEDIA_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {KIND_LABEL[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Linked item</Label>
              <Select value={itemId} onValueChange={setItemId}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {items.map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.activity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Caption</Label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                kind,
                itemId: itemId === 'none' ? null : itemId,
                caption: caption || null,
              })
            }
            disabled={pending}
          >
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
